import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AuthContext, StoreContext } from "../../../Library/contexts";
import { AcceptTermsAndConditions, Loader } from "../../../Pages";
import useInitStore from "./useInitStore";
import useInitServiceWorker from "./useInitServiceWorker";
import { getItemFromLocalStorage } from "../../../Library/helpers";
import config from "../../../config";
import { LIVE_TRACKING_ACC_CODE, SELLER_DES, SUBDEALERS_DES, WEB_APP_PATHS, DRAWER_ACCESS_CODES, SUBSCRIPTION_EXPIRED_DAYS_THRESHOLD, SUBSCRIPTION_SIM_BLOCK_THRESHOLD_DAYS_LEFT } from "../../../Library/constants";
import WebSocketClient from "../../../Library/websocket";
import { unregister } from "../../../Library/worker";
import PATHS from "../../paths";
import { getLatestRouteTrip, getRoutes, getUserSubsWarrantyDetails } from "../../../APIs";
import { transformUserDetails, getDaysLeft } from "../../../Library/helpers";
import { useNavigate } from "react-router-dom";
import DriverLoginOverlay from "../../../Pages/DriverFlow/DriverLoginOverlay";
const { socketHostname, glSocketHostname } = config;

const sharedSubsWarrantyState = {
  uid: null,
  cache: null,
  inFlight: false,
  queue: [],
};

const Authenticated = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useContext(AuthContext);
  const { setProfile } = useContext(AuthContext);

  const DESIGNATION = getItemFromLocalStorage("des");
  const IS_USER = getItemFromLocalStorage("user");
  const ACCESS_CODES = getItemFromLocalStorage("accessCodes");
  const [isLoading, setIsLoading] = useState(true);
  const [constants, setConstants] = useState();
  const [devStock, setDevStock] = useState({ walletStock: {} });
  const [userVehicles, setUserVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicleSharingMap, setVehicleSharingMap] = useState({});
  const [allVehicleStats, setAllVehicleStats] = useState({});
  const [allStates, setAllStates] = useState([])
  const [allGeofences, setAllGeofences] = useState([]);
  const [allOverspeedAlerts, setAllOverspeedAlerts] = useState([]);
  const [allVehicleRoutes, setAllVehicleRoutes] = useState([]);
  const [routesGeofenceMap, setRoutesGeofenceMap] = useState({});
  const [routeTriggers, setRouteTriggers] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [allSubUsers, setAllSubUsers] = useState([]);
  const [lastSearchedLoc, setLastSearchedLoc] = useState(null);
  const [lastViewState, setLastViewState] = useState({});
  const [allDealers, setAllDealers] = useState([]);
  const [subsDevs, setSubsDevs] = useState({});
  const [subsRenewals, setSubsRenewals] = useState({});
  const [activeRouteTrip, setActiveRouteTrip] = useState(null);
  const [hasExpiredSubs, setHasExpiredSubs] = useState(false);
  const [activeDriver, setActiveDriver] = useState(() => {
    if (!profile?.driverLogin) return null;
    try { return JSON.parse(localStorage.getItem('driverInfo') || 'null'); } catch(e) { return null; }
  });
  const [driverPinEnteredAt, setDriverPinEnteredAt] = useState(() => {
    if (!profile?.driverLogin) return null;
    const stored = localStorage.getItem('driverPinEnteredAt');
    return stored ? parseInt(stored, 10) : null;
  });

  // Sync activeDriver → localStorage (driverLogin accounts only)
  useEffect(() => {
    if (!profile?.driverLogin) return;
    if (activeDriver) localStorage.setItem('driverInfo', JSON.stringify(activeDriver));
    else localStorage.removeItem('driverInfo');
  }, [activeDriver]);

  // Sync driverPinEnteredAt → localStorage (driverLogin accounts only)
  useEffect(() => {
    if (!profile?.driverLogin) return;
    if (driverPinEnteredAt) localStorage.setItem('driverPinEnteredAt', String(driverPinEnteredAt));
    else localStorage.removeItem('driverPinEnteredAt');
  }, [driverPinEnteredAt]);

  // Pre-fetched data for the driver login overlay (only when driverLogin account)
  const prefetchedRoutesRef = useRef(null);
  const prefetchedActiveTripRef = useRef(null);
  const [prefetchDone, setPrefetchDone] = useState(false);

  const [filters, setFilters] = useState();
  const [notifications, setNotifications] = useState({ unreadCount: 0, notifs: [] });
  const [ltLastSeenData, setLtLastSeenData] = useState(null);
  const isLiveTrackingEnabled = ACCESS_CODES.includes(LIVE_TRACKING_ACC_CODE);
  useInitStore(setConstants, setFilters, setIsLoading, setUserVehicles, setLtLastSeenData, setDrivers);
  useInitServiceWorker(setNotifications, !!profile?.user, !!profile?.webPushAllowed);

  const _updateExpiredSubsFlag = useCallback((data) => {
    const vehs = transformUserDetails(data);
    setHasExpiredSubs(vehs.some(v => {
      const days = getDaysLeft(v.subscriptionExpiry);
      return days <= SUBSCRIPTION_EXPIRED_DAYS_THRESHOLD && days > SUBSCRIPTION_SIM_BLOCK_THRESHOLD_DAYS_LEFT;
    }));
  }, []);

  const ensureSubsWarrantyData = useCallback((options = {}, callback) => {
    const cb = typeof callback === "function" ? callback : () => {};
    const forceRefresh = !!options.forceRefresh;
    const uid = profile?.idx || null;

    if (sharedSubsWarrantyState.uid !== uid) {
      sharedSubsWarrantyState.uid = uid;
      sharedSubsWarrantyState.cache = null;
      sharedSubsWarrantyState.inFlight = false;
      sharedSubsWarrantyState.queue = [];
    }

    if (!forceRefresh && sharedSubsWarrantyState.cache) {
      cb(null, sharedSubsWarrantyState.cache);
      return;
    }

    sharedSubsWarrantyState.queue.push(cb);
    if (sharedSubsWarrantyState.inFlight) return;

    const requestUid = uid;
    sharedSubsWarrantyState.inFlight = true;
    getUserSubsWarrantyDetails(null, (err, data) => {
      if (sharedSubsWarrantyState.uid !== requestUid) return;
      sharedSubsWarrantyState.inFlight = false;
      if (!err && data) {
        sharedSubsWarrantyState.cache = data;
        _updateExpiredSubsFlag(data);
      }

      const queued = sharedSubsWarrantyState.queue.splice(0);
      queued.forEach(fn => {
        try {
          fn(err, err ? sharedSubsWarrantyState.cache : data);
        } catch (e) {
          console.error(e);
        }
      });
    });
  }, [_updateExpiredSubsFlag, profile?.idx]);

  useEffect(() => {
    if (!ACCESS_CODES.includes(DRAWER_ACCESS_CODES.PLANS_AND_WARRANTY)) return;
    ensureSubsWarrantyData();
  }, [ensureSubsWarrantyData]);

  const initWebSocket = () => {
    const isSellerOrSubDealer = (DESIGNATION === SELLER_DES || DESIGNATION === SUBDEALERS_DES);

    if ((!global.WS || !global.WS.wsConnected) && isSellerOrSubDealer) {
      const sellerWsConfig = { url: `${socketHostname}/wss`, pingPong: false, isSellerWs: true };
      global.WS = new WebSocketClient(sellerWsConfig);
    }

    let isUserPath = WEB_APP_PATHS.some(path => location.pathname.includes(path));
    if ((!global.UWS || !global.UWS.wsConnected) && (isUserPath || IS_USER)) {
      const userWsConfig = { url: `${glSocketHostname}/wss?cookie=undefined&fromWeb=true&reqver=12.50`, pingPong: true, isUserWs: true };
      global.UWS = new WebSocketClient(userWsConfig);
    }
  };

  useEffect(() => {
    initWebSocket();
  }, [location.pathname]);

  // When this is a driverLogin account: pre-fetch routes + latest trip in background
  // while the user is looking at the loading animation / PIN entry screen.
  useEffect(() => {
    if (!profile?.driverLogin) return;

    let routesDone = false;
    let tripDone = false;

    const checkBothDone = () => {
      if (routesDone && tripDone) setPrefetchDone(true);
    };

    getRoutes({ driver: true }, (err, data) => {
      if (!err && data) prefetchedRoutesRef.current = data.filter(r => !r.archived);
      routesDone = true;
      checkBothDone();
    });

    getLatestRouteTrip({ forAccount: true }, (err, data) => {
      if (!err && data) prefetchedActiveTripRef.current = data;
      tripDone = true;
      checkBothDone();
    });
  }, [profile?.driverLogin]);

  // Force-navigate to Navigation if driver lands on SelectBus with an ongoing trip.
  // Fires at login (prefetchDone) and whenever user navigates back to SelectBus from the drawer.
  // Scoped to SELECT_BUS path only so it never fires on SelectRoute (avoids interfering with PIN modal).
  useEffect(() => {
    if (!profile?.driverLogin || !activeDriver) return;
    const onSelectBus =
      location.pathname.includes(PATHS.SELECT_BUS) ||
      location.pathname === PATHS.DRIVER_ROUTE_CHECK;
    if (!onSelectBus) return;
    // Vehicles/routes load async — wait rather than resolving against empty data.
    if (!prefetchDone || !userVehicles.length) return;

    getLatestRouteTrip({ driverId: activeDriver.idx }, (_err, trip) => {
      if (!trip || trip.status !== 1 || trip.endTs) return;

      const vehicle = userVehicles.find(v =>
        String(v.idx) === String(trip.vid) || String(v.vid) === String(trip.vid)
      );
      const route = (prefetchedRoutesRef.current || []).find(r =>
        String(r.idx) === String(trip.routeId)
      );

      navigate(
        vehicle && route
          ? `${PATHS.DRIVER_ROUTE_CHECK}/${PATHS.NAVIGATION}`
          : `${PATHS.DRIVER_ROUTE_CHECK}/${PATHS.SELECT_BUS}`,
        {
          state: vehicle && route ? { vehicle, route, tripId: trip.idx, driverId: activeDriver.idx } : undefined,
          replace: true,
        }
      );
    });
  }, [location.pathname, activeDriver, prefetchDone, userVehicles]);

  useEffect(() => {
    if (!ACCESS_CODES.includes(DRAWER_ACCESS_CODES.DRIVER_ROUTE_CHECK)) return;

    const fetchActiveTrip = () => {
       getLatestRouteTrip({ driver: true }, (err, data) => {
            if (!err && data && data.status !== 'COMPLETED' && data.status !== 'INCOMPLETE' && data.status !== 2) {
                 setActiveRouteTrip(data);
            } else {
                 setActiveRouteTrip(null);
            }
       });
    }

    fetchActiveTrip();
    const interval = setInterval(fetchActiveTrip, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    if (global.WS && global.WS.wsConnected) global.WS.closeSocket();
    if (global.UWS && global.UWS.wsConnected) global.UWS.closeSocket();

    sharedSubsWarrantyState.uid = null;
    sharedSubsWarrantyState.cache = null;
    sharedSubsWarrantyState.inFlight = false;
    sharedSubsWarrantyState.queue = [];

    unregister();
    setProfile();

    window.localStorage.removeItem("accessCodes");
    window.localStorage.removeItem("des");
    window.localStorage.removeItem("relay");
    window.localStorage.removeItem("driverInfo");
    navigate(PATHS.LOGIN, { replace: true });
  }

  useEffect(() => {
    global.EE.addEventListener(global.EE.EVENT_TYPES.LOGOUT, handleLogout);

    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.LOGOUT, handleLogout);
    }
  }, []);

  const [driverManagementCache, setDriverManagementCache] = useState({
    stats: null,
    statsRange: null,
    summary: {},
    trips: {}
  });

  if (isLoading) return <Loader />;

  const showDriverOverlay = !!(profile?.driverLogin && !activeDriver);

  return (
    <StoreContext.Provider value={{ constants, setConstants, filters, setFilters, notifications, setNotifications, devStock, setDevStock, userVehicles, setUserVehicles, drivers, setDrivers, vehicleSharingMap, setVehicleSharingMap, allVehicleStats, setAllVehicleStats, allStates, setAllStates, allGeofences, setAllGeofences, allVehicleRoutes, setAllVehicleRoutes, routesGeofenceMap, setRoutesGeofenceMap, routeTriggers, setRouteTriggers, isLiveTrackingEnabled, analytics, setAnalytics, allSubUsers, setAllSubUsers, ltLastSeenData, setLtLastSeenData, lastViewState, setLastViewState, lastSearchedLoc, setLastSearchedLoc, allOverspeedAlerts, setAllOverspeedAlerts, allDealers, setAllDealers, subsDevs, setSubsDevs, subsRenewals, setSubsRenewals, activeRouteTrip, setActiveRouteTrip, driverManagementCache, setDriverManagementCache, activeDriver, setActiveDriver, driverPinEnteredAt, setDriverPinEnteredAt, hasExpiredSubs, setHasExpiredSubs, ensureSubsWarrantyData }}>
      <Outlet />
      {(!IS_USER && !profile.agTnc) ? <AcceptTermsAndConditions /> : null}
      {showDriverOverlay && (
        <DriverLoginOverlay
          prefetchDone={prefetchDone}
          prefetchedRoutes={prefetchedRoutesRef}
          prefetchedActiveTrip={prefetchedActiveTripRef}
          onDismiss={(driver, ts) => {
            setActiveDriver(driver);
            setDriverPinEnteredAt(ts);
          }}
        />
      )}
    </StoreContext.Provider>
  )
};

export default Authenticated;
